import { fetchRatesForBedbankOffersById, getBedbankRateKey } from 'actions/BedbankOfferActions'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from './reduxHooks'

interface BedbankRatesMap {
  [offerId: string]: {
    hotelOnlyRates: Array<App.BedbankRate>
    hotelBundleRates: Array<App.BedbankRate>
  }
}

interface FetchingStatusMap {
  [offerId: string]: boolean
}

function useBedbanksRates(
  offerIds: Array<string>,
  occupants?: Array<App.Occupants>,
  checkIn?: string,
  checkOut?: string,
  hookEnabled: boolean = true,
): [BedbankRatesMap, FetchingStatusMap] {
  const dispatch = useAppDispatch()
  const filterKey = checkIn && checkOut && occupants ? getBedbankRateKey(occupants, checkIn, checkOut) : ''

  // Get rates and fetching status for all offer IDs
  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)
  const offerRatesLoading = useAppSelector(state => state.offer.offerRatesLoading)

  useEffect(() => {
    // We can't get rates for bedbank properties without the following properties
    if (hookEnabled && checkIn && checkOut && occupants && offerIds.length > 0) {
      dispatch(fetchRatesForBedbankOffersById(offerIds, occupants, checkIn, checkOut))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, offerIds, hookEnabled])

  const ratesMap = useMemo(() => {
    const result: BedbankRatesMap = {}

    offerIds.forEach(offerId => {
      const packages = bedbankOfferRates[offerId]?.[filterKey] ?? []
      const hotelOnlyRates = packages.filter(p => !p.isFlightBundle)
      const hotelBundleRates = packages.filter(p => p.isFlightBundle)

      result[offerId] = {
        hotelOnlyRates,
        hotelBundleRates,
      }
    })

    return result
  }, [offerIds, bedbankOfferRates, filterKey])

  const fetchingMap = useMemo(() => {
    const result: FetchingStatusMap = {}

    offerIds.forEach(offerId => {
      result[offerId] = offerRatesLoading[offerId] ?? false
    })

    return result
  }, [offerIds, offerRatesLoading])

  return [ratesMap, fetchingMap]
}

export default useBedbanksRates
